import React, {
    Fragment,
    useContext,
    useEffect,
    useState,
} from 'react';
import { Portal } from 'react-portal';
import classNames from 'classnames';

import { LocaleContext } from 'app/containers/LocaleProvider';

import { Mobile, Desktop } from 'app/components/Responsive';

import { isDateInRange } from 'app/containers/contexts/filters.context/filters.context';
import { filterHiddenPeriods } from '../../../hiddenPeriods';
import { ConfigContext } from '../ConfigProvider';
import { FiltersContext } from '../FiltersProvider';

const desktopSelectPeriodsNode = document.getElementById('global-header-period-area');
const mobileSelectPeriodsNode = document.getElementById('promo-header-period');

export default function PeriodSelect() {
    const {
        pageId,
        firstPeriodStart,
        periods: configPeriods,
        areAvaliableDishesPersistentForPeriods,
        isPeriodSelectHidden,
    } = useContext(ConfigContext);
    const { visiblePeriods } = useContext(FiltersContext);

    const { locale } = useContext(LocaleContext);

    const [isNodesCleared, setIsNodesCleared] = useState(false);
    const [isDropListOpened, setIsDropListOpened] = useState(false);
    const [currentPeriod, setCurrentPeriod] = useState(null);

    useEffect(() => {
        if (desktopSelectPeriodsNode) desktopSelectPeriodsNode.innerHTML = '';
        if (mobileSelectPeriodsNode) mobileSelectPeriodsNode.innerHTML = '';
        setIsNodesCleared(true);
    }, []);

    useEffect(() => {
        const urlObj = new URL(window.location.href);
        const periodFromUrl = urlObj.searchParams.get('period');

        if (periodFromUrl) setCurrentPeriod(periodFromUrl);
    });

    const handleClick = () => setIsDropListOpened(!isDropListOpened);
    // TEMPORARY HIDDEN EASTER PERIOD
    const allPeriods = (configPeriods || visiblePeriods)
        .map((period) => {
            const [startYear, startMonth, startDate] = period.start.split('-');
            const [endYear, endMonth, endDate] = period.end.split('-');
            return {
                title: `${startDate}.${startMonth}-${endDate}.${endMonth}`,
                period: period.start,
                start: period.start,
            };
        }).filter((p) => {
            if (firstPeriodStart) {
                const pd = new Date(p.period);
                const fpd = new Date(firstPeriodStart);
                return pd >= fpd;
            }
            return true;
        });

    // TEMPORARY HIDDEN PERIODS
    // Период может быть выбран через конфиг страницы (firstPeriodStart) или параметр period в url,
    // поэтому скрытые недели убираются только из списка, а текущий период продолжает отображаться
    const periods = filterHiddenPeriods(allPeriods, pageId);

    const isNothingToShow = (
        areAvaliableDishesPersistentForPeriods
        || isPeriodSelectHidden
        || !isNodesCleared
        || !periods
    );

    useEffect(() => {
        try {
            if (mobileSelectPeriodsNode) {
                mobileSelectPeriodsNode.parentElement.classList.toggle('header-actions--empty');
            }
        } catch (e) {
            console.log(e);
        }
    }, [isNothingToShow]);

    if (isNothingToShow) return null;

    const activePeriod = !currentPeriod
        ? periods[0]
        : periods.find((i) => currentPeriod === i.period)
        || allPeriods.find((i) => currentPeriod === i.period);

    // На десктопе активный период не вынесен в отдельную подпись, поэтому
    // выбранная скрытая неделя показывается первым неактивным элементом
    const isActivePeriodHidden = Boolean(activePeriod)
        && Boolean(currentPeriod)
        && !periods.some((i) => currentPeriod === i.period);
    const desktopPeriods = isActivePeriodHidden ? [activePeriod, ...periods] : periods;

    const dropdownClasses = classNames({
        'header-action__city-dropdown': true,
        'is-open': isDropListOpened,
    });
    const currenPeriodClasses = classNames({
        'header-action__city-current': true,
        'is-open': isDropListOpened,
    });

    return (
        <>
            {mobileSelectPeriodsNode && (
                <Mobile isWideMobile>
                    <Portal node={mobileSelectPeriodsNode}>
                        <button
                            className={currenPeriodClasses}
                            onClick={handleClick}
                            type="button"
                        >
                            {locale === 'en' ? activePeriod?.titleEn : activePeriod?.title}
                        </button>

                        <ul className={dropdownClasses}>
                            {periods.map((i) => {
                                const parsedUrl = new URL(window.location.href);
                                parsedUrl.searchParams.set('period', i.period);

                                const href = `/${pageId}/?${parsedUrl.searchParams.toString()}`;

                                const title = locale === 'en' ? i.titleEn : i.title;

                                return (
                                    <li className="header-action__city-dropdown-item" key={i.title}>
                                        <a href={href} className="header-action__city-dropdown-item-link">
                                            {title}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </Portal>
                </Mobile>
            )}

            <Desktop isWideDesktop>
                <Portal node={desktopSelectPeriodsNode}>
                    <div className="header-action__city-text">
                        {desktopPeriods.map((i, index) => {
                            const firstIndex = index === 0;
                            const lastIndex = index === desktopPeriods.length - 1;
                            const width = window.innerWidth <= 1366;

                            const parsedUrl = new URL(window.location.href);
                            const isPeriodActive = (!currentPeriod && firstIndex) || (currentPeriod === i.period);
                            const dividerStyles = classNames({
                                'promo-header-period__divider': true,
                                'header-period--hide': width && index > 0,
                            });
                            const periodStyles = classNames({
                                'header-action__toggle-clickable': !isPeriodActive,
                                'header-action-priod__item': true,
                                'header-period--hide': width && index > 1,
                            });

                            const title = locale === 'en' ? i.titleEn : i.title;

                            if (isPeriodActive) {
                                return (
                                    <Fragment key={i.title}>
                                        <span className={periodStyles}>{title}</span>
                                        {!lastIndex && <span className={dividerStyles}>/</span>}
                                    </Fragment>
                                );
                            }

                            parsedUrl.searchParams.set('period', i.period);
                            const href = `/${pageId}/?${parsedUrl.searchParams.toString()}`;

                            return (
                                <Fragment key={i.title}>
                                    <a
                                        className={periodStyles}
                                        href={href}
                                    >
                                        {title}
                                    </a>
                                    {!lastIndex && <span className={dividerStyles}>/</span>}
                                </Fragment>
                            );
                        })}
                    </div>
                </Portal>
            </Desktop>
        </>
    );
}
